import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Link } from "wouter";
import { toast } from "sonner";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Button } from "@/components/ui/button";
import { authRegisterParent, requestRegistrationOtp, ApiError } from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { setToken, setRefreshToken } from "@/lib/auth";

const formSchema = z
  .object({
    nom: z.string().min(1, "Nom requis"),
    prenoms: z.string().min(1, "Prénoms requis"),
    email: z.string().email("Email invalide"),
    phone: z
      .string()
      .regex(/^\+[1-9]\d{6,14}$/, "Doit commencer par + et le code pays (ex: +2250707070707)"),
    // Règles ALIGNÉES sur `RegisterRequest` (auth-service). Sans elles, un mot
    // de passe simplement long passait la validation du formulaire puis se
    // faisait refuser en 400 à l'étape du code — sans explication.
    password: z
      .string()
      .min(8, "Minimum 8 caractères")
      .max(100, "Maximum 100 caractères")
      .regex(/[A-Z]/, "Ajoutez au moins une majuscule")
      .regex(/[0-9]/, "Ajoutez au moins un chiffre")
      .regex(
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
        "Ajoutez au moins un caractère spécial (! ? * $…)"
      ),
    confirmPassword: z.string().min(8, "Minimum 8 caractères"),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof formSchema>;

export default function Register() {
  const [step, setStep] = useState<"form" | "otp">("form");
  const [pending, setPending] = useState(false);
  const [authUnavailable, setAuthUnavailable] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nom: "",
      prenoms: "",
      email: "",
      phone: "+225",
      password: "",
      confirmPassword: "",
    },
  });

  function handleAuthError(err: unknown) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      setAuthUnavailable(true);
    } else {
      toast.error(friendlyError(err));
    }
  }

  // Étape 1 : valider les infos + demander le code de vérification.
  // Les valeurs validées restent dans le form (non réinitialisé) → source unique
  // pour l'étape 2 via form.getValues().
  async function onRequestOtp(values: FormValues) {
    setPending(true);
    try {
      await requestRegistrationOtp({ email: values.email, phone: values.phone });
      setStep("otp");
      toast.success("Code envoyé par email et SMS.");
    } catch (err) {
      handleAuthError(err);
    } finally {
      setPending(false);
    }
  }

  // Étape 2 : créer le compte avec le code OTP.
  async function onSubmitOtp(e: React.FormEvent) {
    e.preventDefault();
    if (otpCode.trim().length < 4) {
      toast.error("Saisissez le code reçu.");
      return;
    }
    setPending(true);
    try {
      const res = await authRegisterParent({ ...form.getValues(), otpCode: otpCode.trim() });
      setToken(res.accessToken);
      if (res.refreshToken) setRefreshToken(res.refreshToken);
      toast.success("Compte créé avec succès !");
      window.location.assign("/dashboard");
    } catch (err) {
      handleAuthError(err);
    } finally {
      setPending(false);
    }
  }

  async function resendOtp() {
    const { email, phone } = form.getValues();
    try {
      await requestRegistrationOtp({ email, phone });
      toast.success("Nouveau code envoyé.");
    } catch (err) {
      handleAuthError(err);
    }
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <div className="bg-white rounded-3xl p-8 border border-orange-50 shadow-sm">
        <h1 className="text-2xl font-display font-bold text-navy mb-6">
          Créer un compte parent
        </h1>

        {authUnavailable ? (
          <div className="p-4 rounded-xl bg-gold/10 border border-gold/30 text-sm text-navy">
            <p className="font-bold mb-1">Ouverture de compte bientôt disponible</p>
            <p className="text-navy/70">
              Vous pouvez d'ores et déjà payer sans compte. Le reçu vous sera
              envoyé par email et SMS.
            </p>
            <Link
              href="/ecoles"
              className="inline-block mt-4 bg-coral text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-[#d16b50]"
            >
              Trouver mon école
            </Link>
          </div>
        ) : step === "form" ? (
          <>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onRequestOtp)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="nom"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nom</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="prenoms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Prénoms</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Téléphone</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="+225…" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mot de passe</FormLabel>
                      <FormControl>
                        <PasswordInput {...field} />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        8 caractères minimum, dont une majuscule, un chiffre et un
                        caractère spécial (! ? * $…).
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmer le mot de passe</FormLabel>
                      <FormControl>
                        <PasswordInput {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full bg-coral hover:bg-[#d16b50] rounded-xl mt-4 text-white"
                  disabled={pending}
                  data-testid="button-request-otp"
                >
                  {pending ? "Envoi du code…" : "Recevoir le code"}
                </Button>
              </form>
            </Form>
            <div className="mt-6 text-center text-sm text-navy/70">
              Déjà un compte ?{" "}
              <Link
                href="/auth/login"
                className="text-coral font-bold hover:underline"
              >
                Se connecter
              </Link>
            </div>
          </>
        ) : (
          <form onSubmit={onSubmitOtp} className="space-y-4">
            <p className="text-sm text-navy/70">
              Un code de vérification a été envoyé à{" "}
              <span className="font-bold text-navy">{form.getValues("email")}</span> et par
              SMS. Saisissez-le pour finaliser votre inscription.
            </p>
            <div>
              <label className="text-sm font-medium text-navy" htmlFor="otp-code">
                Code de vérification
              </label>
              <Input
                id="otp-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="mt-1 tracking-[0.5em] text-center text-lg"
                data-testid="input-otp-code"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-coral hover:bg-[#d16b50] rounded-xl text-white"
              disabled={pending}
              data-testid="button-submit-otp"
            >
              {pending ? "Création…" : "Créer le compte"}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => setStep("form")}
                className="text-navy/60 hover:underline"
              >
                ← Modifier mes infos
              </button>
              <button
                type="button"
                onClick={resendOtp}
                className="text-coral font-bold hover:underline"
                data-testid="button-resend-otp"
              >
                Renvoyer le code
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
